<template>
  <basic-dialog :show="show" max-width="1400px" @close="onCancel">
    <template v-slot:title>Edit Departure</template>

    <template v-slot:actions>
      <v-btn
        color="primary"
        outlined
        :loading="saving"
        :disabled="!canSubmit"
        @click="onConfirm(false)"
        >Save &amp; Continue</v-btn
      >
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!canSubmit"
        @click="onConfirm(true)"
        >Save</v-btn
      >
    </template>

    <v-row>
      <v-col cols="12" sm="3">
        <date-picker
          v-model="form.startDate"
          custom-label="Start Date"
          outlined
          :range-start="masterValidFrom"
          :range-end="masterValidTo"
          :error-messages="errors.startDate"
        ></date-picker>
      </v-col>
      <v-col cols="12" sm="3">
        <date-picker
          v-model="form.returnDate"
          custom-label="Return Date"
          outlined
          :range-start="form.startDate || masterValidFrom"
          :range-end="masterValidTo"
          :error-messages="errors.returnDate"
        ></date-picker>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="form.name"
          label="Name"
          outlined
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="6" sm="3">
        <v-text-field
          v-model.number="form.adults"
          label="Adults"
          type="number"
          min="0"
          outlined
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="6" sm="3">
        <v-text-field
          v-model.number="form.children"
          label="Children"
          type="number"
          min="0"
          outlined
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="3">
        <brand-list-select v-model="form.brand" outlined></brand-list-select>
      </v-col>
      <v-col cols="12" sm="3">
        <department-list-select
          v-model="form.department"
          outlined
        ></department-list-select>
      </v-col>
      <!-- #11180 — seeded onto services / itineraries booked from this departure -->
      <v-col cols="12" sm="3">
        <service-status-list-select
          :value="form.serviceStatusId"
          :service-status-id="form.serviceStatusId"
          :is-disabled="false"
          :outlined="true"
          @input="form.serviceStatusId = $event"
        ></service-status-list-select>
      </v-col>
      <v-col cols="12" sm="3">
        <itinerary-status-list-select
          v-model="form.internalStatus"
          label="Internal Status"
          :skip-autoselect="true"
        ></itinerary-status-list-select>
      </v-col>
    </v-row>

    <v-divider class="my-4"></v-divider>

    <div class="d-flex align-center mb-2">
      <h3 class="subtitle-1 mb-0">Items</h3>
      <v-spacer></v-spacer>
      <span v-if="$apollo.loading" class="caption text--secondary">
        Loading…
      </span>
    </div>

    <v-data-table
      :headers="itemHeaders"
      :items="items"
      :loading="$apollo.loading"
      item-key="id"
      hide-default-footer
      disable-pagination
      dense
      class="elevation-1"
    >
      <template v-slot:item.day="{ item }">
        <v-text-field
          :value="item.day"
          type="number"
          min="1"
          :max="departureDays"
          dense
          hide-details
          single-line
          @change="onItemFieldChange(item, 'day', $event)"
          style="max-width: 60px"
        ></v-text-field>
      </template>

      <template v-slot:item.duration="{ item }">
        <v-text-field
          :value="item.duration"
          type="number"
          min="1"
          :max="itemMaxDuration(item)"
          dense
          hide-details
          single-line
          @change="onItemFieldChange(item, 'duration', $event)"
          style="max-width: 60px"
        ></v-text-field>
      </template>

      <template v-slot:item.service="{ item }">
        {{ item.service && item.service.name }}
      </template>

      <template v-slot:item.supplier="{ item }">
        {{ item.supplier && item.supplier.name }}
      </template>

      <template v-slot:item.qty="{ item }">
        <v-text-field
          :value="item.qty"
          type="number"
          min="0"
          dense
          hide-details
          single-line
          @change="onItemFieldChange(item, 'qty', $event)"
          style="max-width: 70px"
        ></v-text-field>
      </template>

      <template v-slot:item.adults="{ item }">
        <v-text-field
          :value="item.adults"
          type="number"
          min="0"
          dense
          hide-details
          single-line
          @change="onItemFieldChange(item, 'adults', $event)"
          style="max-width: 70px"
        ></v-text-field>
      </template>

      <template v-slot:item.children="{ item }">
        <v-text-field
          :value="item.children"
          type="number"
          min="0"
          dense
          hide-details
          single-line
          @change="onItemFieldChange(item, 'children', $event)"
          style="max-width: 70px"
        ></v-text-field>
      </template>

      <template v-slot:item.included="{ item }">
        <v-simple-checkbox
          :value="item.included"
          @input="onItemFlagChange(item, 'included', $event)"
        ></v-simple-checkbox>
      </template>

      <template v-slot:item.optional="{ item }">
        <v-simple-checkbox
          :value="item.optional"
          @input="onItemFlagChange(item, 'optional', $event)"
        ></v-simple-checkbox>
      </template>

      <template v-slot:item.price="{ item }">
        <v-btn
          small
          text
          :color="item.customPrice ? 'warning' : 'success'"
          @click="onOpenPricing(item)"
        >
          <v-icon left small>mdi-cash-multiple</v-icon>
          {{ item.customPrice ? "Custom" : "Live" }}
        </v-btn>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" @click="onOpenRemarks(item)">
              <v-icon small color="primary">mdi-note-outline</v-icon>
            </v-btn>
          </template>
          <span>Remarks</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" @click="onDeleteItem(item)">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>

      <template v-slot:no-data>
        <div class="pa-3 text--secondary">No items.</div>
      </template>
    </v-data-table>

    <v-divider class="my-4"></v-divider>

    <h3 class="subtitle-1 mb-2">Add Service</h3>
    <open-group-item-quick-add
      :destination-id="destinationId"
      :group-duration="masterDuration"
      :group-adults="Number(form.adults) || 0"
      :group-children="Number(form.children) || 0"
      @onAddItem="onAddItem"
    ></open-group-item-quick-add>

    <open-group-departure-item-pricing-dialog
      :show="pricingDialog"
      :item="pricingItem"
      :departure-start-date="form.startDate"
      :department="form.department"
      @saved="onPricingSaved"
      @close="pricingDialog = false"
    ></open-group-departure-item-pricing-dialog>

    <remarks-edit-dialog
      :show="remarksDialog"
      :value="remarksValue"
      @save="onSaveRemarks"
      @close="remarksDialog = false"
    ></remarks-edit-dialog>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog";
import DatePicker from "../../shared/DatePicker";
import BrandListSelect from "../../shared/BrandListSelect";
import DepartmentListSelect from "../../shared/DepartmentListSelect";
import OpenGroupDepartureItemPricingDialog from "./OpenGroupDepartureItemPricingDialog";
import OpenGroupItemQuickAdd from "./OpenGroupItemQuickAdd";
import RemarksEditDialog from "../../itineraries/builder/tools/RemarksEditDialog";
import ServiceStatusListSelect from "../../shared/ServiceStatusListSelect";
import ItineraryStatusListSelect from "../../shared/ItineraryStatusListSelect";
import { removeGQLTypename } from "../../../../helpers";
import {
  OPEN_GROUP_DEPARTURE_QUERY,
  UPDATE_OPEN_GROUP_DEPARTURE,
  UPDATE_OPEN_GROUP_DEPARTURE_ITEM,
  DELETE_OPEN_GROUP_DEPARTURE_ITEM,
  ADD_OPEN_GROUP_DEPARTURE_ITEM,
} from "../departure-gql";

export default {
  name: "OpenGroupDepartureEditDialog",

  components: {
    BasicDialog,
    DatePicker,
    BrandListSelect,
    DepartmentListSelect,
    OpenGroupDepartureItemPricingDialog,
    OpenGroupItemQuickAdd,
    RemarksEditDialog,
    ServiceStatusListSelect,
    ItineraryStatusListSelect,
  },

  props: {
    show: { type: Boolean, default: false },
    departure: { type: Object, default: null },
    masterValidFrom: { type: String, default: null },
    masterValidTo: { type: String, default: null },
  },

  data() {
    return {
      saving: false,
      errors: {},
      form: this.blankForm(),
      openGroupDeparture: null,
      pricingDialog: false,
      pricingItem: null,
      remarksDialog: false,
      remarksItem: null,
      remarksValue: null,
      itemHeaders: [
        { text: "Day", value: "day", width: 70 },
        { text: "Dur", value: "duration", width: 70 },
        { text: "Service", value: "service" },
        { text: "Supplier", value: "supplier" },
        { text: "Qty", value: "qty", width: 90 },
        { text: "Adults", value: "adults", width: 90 },
        { text: "Children", value: "children", width: 90 },
        { text: "Included", value: "included", width: 80, sortable: false },
        { text: "Optional", value: "optional", width: 80, sortable: false },
        { text: "Price", value: "price", width: 90, sortable: false },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
          width: 110,
        },
      ],
    };
  },

  computed: {
    canSubmit() {
      if (this.saving) return false;
      return !!(this.form.startDate && this.form.returnDate);
    },
    items() {
      if (!this.openGroupDeparture || !this.openGroupDeparture.items) return [];
      return this.openGroupDeparture.items.map(removeGQLTypename);
    },
    masterOpenGroup() {
      return (
        (this.openGroupDeparture && this.openGroupDeparture.openGroup) ||
        (this.departure && this.departure.openGroup) ||
        null
      );
    },
    masterDuration() {
      return (this.masterOpenGroup && this.masterOpenGroup.duration) || 7;
    },
    departureDays() {
      if (!this.form.startDate || !this.form.returnDate) return 1;
      const start = new Date(this.form.startDate);
      const ret = new Date(this.form.returnDate);
      const diff = Math.round((ret - start) / 86400000);
      return diff >= 0 ? diff + 1 : 1;
    },
    destinationId() {
      return this.masterOpenGroup &&
        this.masterOpenGroup.destination &&
        this.masterOpenGroup.destination.id
        ? this.masterOpenGroup.destination.id
        : null;
    },
  },

  watch: {
    show(open) {
      if (open) this.loadForm();
    },
  },

  methods: {
    blankForm() {
      return {
        name: "",
        startDate: "",
        returnDate: "",
        adults: 0,
        children: 0,
        brand: "",
        department: "",
        serviceStatusId: "",
        internalStatus: "",
      };
    },
    loadForm() {
      this.errors = {};
      const d = this.departure || {};
      this.form = {
        name: d.name || "",
        startDate: d.startDate ? d.startDate.substring(0, 10) : "",
        returnDate: d.returnDate ? d.returnDate.substring(0, 10) : "",
        adults: d.adults != null ? d.adults : 0,
        children: d.children != null ? d.children : 0,
        brand: d.brand || "",
        department: d.department || "",
        serviceStatusId: d.serviceStatusId || "",
        internalStatus: d.internalStatus || "",
      };
    },
    onCancel() {
      this.$emit("close");
    },
    onConfirm(closeAfter) {
      if (!this.departure) return;
      this.errors = {};
      this.saving = true;
      this.$apollo
        .mutate({
          mutation: UPDATE_OPEN_GROUP_DEPARTURE,
          variables: {
            id: this.departure.id,
            openGroupDepartureInput: {
              name: this.form.name,
              startDate: this.form.startDate,
              returnDate: this.form.returnDate,
              adults: Number(this.form.adults) || 0,
              children: Number(this.form.children) || 0,
              brand: this.form.brand || "",
              department: this.form.department || "",
              serviceStatusId: this.form.serviceStatusId || null,
              internalStatus: this.form.internalStatus || null,
            },
          },
        })
        .then(({ data }) => {
          this.saving = false;
          this.$emit("updated", data.updateOpenGroupDeparture);
          if (closeAfter) {
            this.$emit("close");
          } else {
            this.$apollo.queries.openGroupDeparture.refetch();
          }
        })
        .catch((err) => {
          this.saving = false;
          this.flashError(err);
        });
    },
    itemMaxDuration(item, day) {
      const d = Number(day != null ? day : item.day) || 1;
      let max = this.departureDays - d;
      const isAccom =
        item.supplier && item.supplier.serviceType === "ACCOMMODATION";
      if (!isAccom) max += 1; // non-accom is day-inclusive; accom counts nights
      return max < 1 ? 1 : max;
    },
    onItemFieldChange(item, field, value) {
      let num = Number(value);
      if (Number.isNaN(num)) return;
      const input = {};
      if (field === "day") {
        if (num < 1) num = 1;
        if (num > this.departureDays) num = this.departureDays;
        input.day = num;
        // shrink duration if it now overruns the return date
        const durMax = this.itemMaxDuration(item, num);
        if (Number(item.duration) > durMax) input.duration = durMax;
      } else if (field === "duration") {
        if (num < 1) num = 1;
        const max = this.itemMaxDuration(item);
        if (num > max) num = max;
        input.duration = num;
      } else {
        input[field] = num;
      }
      this.$apollo
        .mutate({
          mutation: UPDATE_OPEN_GROUP_DEPARTURE_ITEM,
          variables: {
            id: item.id,
            openGroupDepartureItemInput: input,
          },
        })
        .then(() => {
          this.$apollo.queries.openGroupDeparture.refetch();
        })
        .catch(this.flashError);
    },
    onItemFlagChange(item, field, value) {
      this.$apollo
        .mutate({
          mutation: UPDATE_OPEN_GROUP_DEPARTURE_ITEM,
          variables: {
            id: item.id,
            openGroupDepartureItemInput: { [field]: !!value },
          },
        })
        .then(() => {
          this.$apollo.queries.openGroupDeparture.refetch();
        })
        .catch(this.flashError);
    },
    onDeleteItem(item) {
      if (!window.confirm(`Remove ${item.service && item.service.name}?`))
        return;
      this.$apollo
        .mutate({
          mutation: DELETE_OPEN_GROUP_DEPARTURE_ITEM,
          variables: { id: item.id },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Item removed.",
          });
          this.$apollo.queries.openGroupDeparture.refetch();
        })
        .catch(this.flashError);
    },
    onOpenPricing(item) {
      this.pricingItem = item;
      this.pricingDialog = true;
    },
    onOpenRemarks(item) {
      this.remarksItem = item;
      this.remarksValue = {
        remarks: item.remarks ? { ...item.remarks } : {},
        serviceType: item.supplier && item.supplier.serviceType,
        serviceName: item.service && item.service.name,
        supplierName: item.supplier && item.supplier.name,
        rateTypeCode: item.rateTypeCode || "",
        qty: item.qty != null ? item.qty : 1,
        adults: item.adults != null ? item.adults : 0,
        children: item.children != null ? item.children : 0,
        infants: 0,
      };
      this.remarksDialog = true;
    },
    onSaveRemarks(remarks) {
      if (!this.remarksItem) return;
      const id = this.remarksItem.id;
      this.$apollo
        .mutate({
          mutation: UPDATE_OPEN_GROUP_DEPARTURE_ITEM,
          variables: {
            id,
            openGroupDepartureItemInput: { remarks: removeGQLTypename(remarks) },
          },
        })
        .then(() => {
          this.remarksDialog = false;
          this.$store.dispatch("showAppSnackbar", { msg: "Remarks saved." });
          this.$apollo.queries.openGroupDeparture.refetch();
        })
        .catch(this.flashError);
    },
    onAddItem(payload) {
      if (!this.departure) return;
      this.$apollo
        .mutate({
          mutation: ADD_OPEN_GROUP_DEPARTURE_ITEM,
          variables: {
            openGroupDepartureId: this.departure.id,
            openGroupDepartureItemInput: {
              serviceId: payload.serviceId,
              supplierId: payload.supplierId,
              uid: payload.uid,
              parentUid: payload.parentUid,
              day: Number(payload.startDay),
              duration: Number(payload.duration),
              order: Number(payload.order) || 0,
              qty: Number(payload.qty) || 1,
              adults: Number(payload.adults) || 0,
              children: Number(payload.children) || 0,
              optional: payload.optional || false,
              included: payload.included !== false,
              hidden: payload.hidden || false,
              includeMarkup: payload.includeMarkup || false,
              ratePlan: payload.ratePlan || "",
            },
          },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", { msg: "Service added." });
          this.$apollo.queries.openGroupDeparture.refetch();
        })
        .catch(this.flashError);
    },
    onPricingSaved() {
      this.$store.dispatch("showAppSnackbar", { msg: "Pricing saved." });
      this.$apollo.queries.openGroupDeparture.refetch();
    },
    flashError(err) {
      const msg = err.graphQLErrors
        ? err.graphQLErrors.map((e) => e.message).join(", ")
        : err.message;
      this.$store.dispatch("showAppSnackbar", { msg, color: "error" });
    },
  },

  apollo: {
    openGroupDeparture: {
      query: OPEN_GROUP_DEPARTURE_QUERY,
      variables() {
        return { id: this.departure && this.departure.id };
      },
      skip() {
        return !this.show || !this.departure;
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/builder/OpenGroupDepartureEditDialog.vue